import { useState, useRef, useMemo } from "react";
import { Badge } from "react-bootstrap";
import { useFetch } from "../../../hooks/api";
import Loading from "../../../components/core/blocks/Loading";
import { Accordion } from "../../../components/core/basic/Accordion";
import ReactTablePagination from "../../../components/custom/growth/ProductsCVTablePagination";
import useColumnSelector from "../../../hooks/useColumnSelector";
import ReactTable from "react-table-6";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import useLifecycleFilter from "../../../hooks/useLifecycleFilter";
import useCustomTableFilters from "../../../hooks/useCustomTableFilters";
import useTableSettings from "../../../hooks/useTableSettings";
import { RT6FilterWidget } from "../../merchandising/components/tables/FilterWidget";
import { sortTableColumns } from "../../../utils/tableUtils";

const TemplatesSection = () => {
  const { t } = useTranslation(["product_quality"]);
  const fmt = useDateFormatter("shortDateTime");
  const tableRef = useRef();

  const [searchFilter, setSearchFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading } = useFetch(
    ["content_templates"],
    `/merchandise/content_templates/`,
    {},
    {
      select: (d) => d.data?.results || {},
    },
  );

  const TABLE_ID = "productSEOTemplatesTable";

  const { savedTableSettings: savedTablePageSize, updateTableSettings: setSavedTablePageSize } = useTableSettings(
    TABLE_ID,
    "tablePageSize",
    5,
  );

  const { savedTableSettings: savedTableSortMethod, updateTableSettings: setSavedTableSortMethod } = useTableSettings(
    TABLE_ID,
    "tableSorting",
    [],
  );

  const { savedTableSettings: savedColumnOrder, updateTableSettings: setSavedColumnOrder } = useTableSettings(
    TABLE_ID,
    "tableColOrder",
    [],
  );

  const [columns, columnSelectorProps] = useColumnSelector(() => {
    const specifiedColumns = [
      {
        id: "name",
        accessor: "name",
        Header: () => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              textAlign: "left",
            }}
          >
            {t(($) => $.product_quality["Name"])}
          </div>
        ),
        style: { textAlign: "left" },
        grouping: "configuration",
        toolTip: t(($) => $.product_quality["The name of the template"]),
        Cell: ({ value, original }) => (
          <Link
            to="/user/content/templates/$templateId"
            params={{ templateId: String(original.id) }}
            className="text-decoration-none"
          >
            <strong>{value}</strong>
          </Link>
        ),
        width: 250,
      },
      {
        id: "status",
        Header: t(($) => $.product_quality["Status"]),
        accessor: "revision",
        grouping: "configuration",
        toolTip: t(($) => $.product_quality["The status the template"]),
        Cell: ({ value }) => (value ? t(($) => $.product_quality["Draft"]) : "-"),
      },
      {
        id: "created_by",
        Header: t(($) => $.product_quality["Created by"]),
        accessor: "created_by",
        format: "text",
        grouping: "configuration",
        toolTip: t(($) => $.product_quality["The creator of the template"]),
        Cell: ({ value: { first_name, last_name } }) => `${first_name} ${last_name}`,
      },
      {
        id: "created_on",
        Header: t(($) => $.product_quality["Created"]),
        accessor: "created_on",
        grouping: "configuration",
        toolTip: t(($) => $.product_quality["The date the template was created"]),
        Cell: ({ value }) => (value ? fmt.format(new Date(value)) : "-"),
      },
      {
        id: "updated_on",
        Header: t(($) => $.product_quality["Updated"]),
        accessor: "updated_on",
        grouping: "configuration",
        toolTip: t(($) => $.product_quality["The date the template was last updated"]),
        Cell: ({ value }) => (value ? fmt.format(new Date(value)) : "-"),
      },
    ];

    return sortTableColumns(specifiedColumns, savedColumnOrder);
  }, []);

  const { customFilters, setCustomFilters, applyCustomFilters } = useCustomTableFilters(TABLE_ID);

  const searchFilteredData = useMemo(() => {
    return applyCustomFilters(data || [])
      .filter(({ name }) => {
        if (searchFilter) {
          if (name.toLowerCase().includes(searchFilter.toLowerCase())) {
            return true;
          }
          return false;
        }
        return true;
      })
      .sort((a, b) => b.seo_score_avg - a.seo_score_avg);

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [data, searchFilter, customFilters]);
  const { filteredData: tableData } = useLifecycleFilter(searchFilteredData);

  return (
    <Accordion
      title={
        <>
          {t(($) => $.product_quality["Templates"])}{" "}
          <Badge className="bg-trellis-purple ms-2 fs-6" pill>
            {t(($) => $.product_quality["ALPHA"])}
          </Badge>
        </>
      }
      defaultExpanded
    >
      {isLoading ?
        <Loading />
      : <ReactTable
          ref={tableRef}
          data={tableData}
          defaultPageSize={savedTablePageSize || 10}
          minRows={5}
          className={`pricing-table ${TABLE_ID}`}
          PaginationComponent={ReactTablePagination}
          getPaginationProps={() => ({
            updateSearch: setSearchFilter,
            showFilters: showFilters,
            setShowFilters: setShowFilters,
            clearFilters: () => {
              setSearchFilter("");
            },
            hideFilters: false,
            clearSearchTermFilters: () => setSearchFilter(""),
            searchTerm: searchFilter,
            table: "plan-products",
            style: { marginBottom: "2rem" },
            carouselLayout: false,
            disableSearchFocus: true,
            filterWidget: true,
            hideFilterToggleIcon: true,
            savedColumnOrder: savedColumnOrder,
            setSavedColumnOrder: setSavedColumnOrder,
            children: (
              <div className="d-flex justify-content-start w-100 h-100 align-items-center px-2">
                <RT6FilterWidget
                  customFilters={customFilters}
                  setCustomFilters={setCustomFilters}
                  tableRef={tableRef}
                  excludedFilterColumns={["name"]}
                />
              </div>
            ),
          })}
          filterable={showFilters}
          onSortedChange={(newSorted) => {
            setSavedTableSortMethod(newSorted);
          }}
          onPageSizeChange={(pageSize) => {
            setSavedTablePageSize(pageSize);
          }}
          columns={columns}
          NoDataComponent={() => null}
          showPaginationTop
          showPaginationBottom={false}
          columnSelectorProps={columnSelectorProps}
          defaultSorted={
            savedTableSortMethod || [
              {
                id: "updated_on",
                desc: true,
              },
            ]
          }
        />
      }
    </Accordion>
  );
};

export default TemplatesSection;
